import React, { useEffect, useMemo, useState } from "react";
import {
  deleteRow,
  getDocumentDownloadUrl,
  getSession,
  insertRow,
  isSupabaseConfigured,
  loadAdminData,
  onAuthChange,
  signIn,
  signOut,
  updateRow,
  verifyAdmin,
} from "../lib/data";
import { Brand, Icon } from "./Icons";
import CaseManagement from "./CaseManagement";
import { InvoiceWorkspace } from "./InvoiceManagement";
import { LanguageSwitcher, useI18n } from "../lib/i18n";

const adminNavigation = [
  { id: "dashboard", label: "Dashboard", icon: "home" },
  { id: "cases", label: "Rastet", icon: "briefcase" },
  { id: "tasks", label: "Detyrat", icon: "checklist" },
  { id: "billing", label: "Faturat", icon: "document" },
  { id: "messages", label: "Mesazhet", icon: "message" },
  { id: "consultations", label: "Konsultimet", icon: "calendar" },
  { id: "services", label: "Shërbimet", icon: "grid" },
  { id: "resources", label: "Burime Ligjore", icon: "book" },
  { id: "articles", label: "Artikujt", icon: "articles" },
  { id: "profile", label: "Profili", icon: "user" },
  { id: "branding", label: "Brandimi", icon: "scale" },
];

const serviceFields = [
  { name: "title", label: "Titulli (Shqip)", required: true },
  { name: "title_en", label: "Title (English)", required: true },
  { name: "title_sr", label: "Naslov (Srpski)", required: true },
  { name: "description", label: "Përshkrimi (Shqip)", type: "textarea" },
  { name: "description_en", label: "Description (English)", type: "textarea" },
  { name: "description_sr", label: "Opis (Srpski)", type: "textarea" },
  { name: "icon", label: "Ikona (p.sh. scale, shield, family)" },
  { name: "sort_order", label: "Renditja", type: "number" },
  { name: "active", label: "Shfaqet publikisht", type: "checkbox" },
];

const resourceFields = [
  { name: "title", label: "Titulli (Shqip)", required: true },
  { name: "title_en", label: "Title (English)", required: true },
  { name: "title_sr", label: "Naslov (Srpski)", required: true },
  { name: "description", label: "Përshkrimi (Shqip)", type: "textarea" },
  { name: "description_en", label: "Description (English)", type: "textarea" },
  { name: "description_sr", label: "Opis (Srpski)", type: "textarea" },
  { name: "url", label: "URL zyrtare", type: "url", required: true },
  { name: "active", label: "Shfaqet publikisht", type: "checkbox" },
];

const articleFields = [
  { name: "title", label: "Titulli (Shqip)", required: true },
  { name: "title_en", label: "Title (English)", required: true },
  { name: "title_sr", label: "Naslov (Srpski)", required: true },
  { name: "content", label: "Përmbajtja (Shqip)", type: "textarea", rows: 7 },
  { name: "content_en", label: "Content (English)", type: "textarea", rows: 7 },
  { name: "content_sr", label: "Sadržaj (Srpski)", type: "textarea", rows: 7 },
  { name: "image_url", label: "URL e fotografisë", type: "url" },
  { name: "published", label: "Publikuar", type: "checkbox" },
];

const profileFields = [
  { name: "name", label: "Emri dhe mbiemri", required: true },
  { name: "title", label: "Titulli profesional (Shqip)" },
  { name: "title_en", label: "Professional title (English)" },
  { name: "title_sr", label: "Profesionalna titula (Srpski)" },
  { name: "bio", label: "Biografia (Shqip)", type: "textarea", rows: 7 },
  { name: "bio_en", label: "Biography (English)", type: "textarea", rows: 7 },
  { name: "bio_sr", label: "Biografija (Srpski)", type: "textarea", rows: 7 },
  { name: "phone", label: "Telefoni" },
  { name: "whatsapp", label: "WhatsApp" },
  { name: "viber", label: "Viber" },
  { name: "email", label: "Email", type: "email" },
  { name: "photo_url", label: "URL e fotografisë", type: "url" },
];

const brandingFields = [
  { name: "logo_name", label: "Emri i logos" },
  { name: "logo_url", label: "URL e logos", type: "url" },
  { name: "hero_image", label: "URL e fotografisë hero", type: "url" },
  { name: "primary_colour", label: "Ngjyra kryesore", type: "color" },
  { name: "secondary_colour", label: "Ngjyra dytësore", type: "color" },
  { name: "is_active", label: "Logo aktive", type: "checkbox" },
];

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("sq-AL", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function statusLabel(value) {
  return String(value || "NEW").replaceAll("_", " ");
}

function DataNotice({ dataset }) {
  if (dataset?.table) return null;
  return (
    <div className="admin-empty admin-empty--warning">
      <Icon name="warning" size={22} />
      <div>
        <strong>Tabela nuk është e disponueshme</strong>
        <p>{dataset?.error?.message || "Kontrolloni skemën dhe politikat RLS në Supabase."}</p>
      </div>
    </div>
  );
}

function LoginView({ onAuthenticated }) {
  const { t } = useI18n();
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [state, setState] = useState({ busy: false, error: "" });

  const submit = async (event) => {
    event.preventDefault();
    setState({ busy: true, error: "" });
    try {
      const session = await signIn(credentials.email.trim(), credentials.password);
      onAuthenticated(session);
    } catch (error) {
      setState({ busy: false, error: error.message });
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-language"><LanguageSwitcher compact /></div>
      <a className="admin-back-link" href="/">← {t("Kthehu në faqen publike")}</a>
      <section className="admin-login-visual">
        <Brand />
        <div>
          <span className="eyebrow">Panel i mbrojtur</span>
          <h1>Menaxhoni rastet, afatet, dokumentet dhe komunikimin në një vend.</h1>
          <p>Hyrja verifikohet me Supabase Auth; çdo dosje dhe dokument mbrohet nga politikat RLS të bazës së të dhënave.</p>
        </div>
      </section>
      <section className="admin-login-card">
        <span className="icon-badge"><Icon name="lock" size={28} /></span>
        <h2>Admin Login</h2>
        <p>Hyni me llogarinë ekzistuese të administratës.</p>
        {!isSupabaseConfigured ? (
          <div className="admin-inline-error">Supabase nuk është konfiguruar. Plotësoni `.env` me URL-në dhe çelësin publik.</div>
        ) : null}
        <form onSubmit={submit}>
          <label>Email<input type="email" value={credentials.email} onChange={(event) => setCredentials({ ...credentials, email: event.target.value })} required autoComplete="username" /></label>
          <label>Fjalëkalimi<input type="password" value={credentials.password} onChange={(event) => setCredentials({ ...credentials, password: event.target.value })} required autoComplete="current-password" /></label>
          <button className="button button--gold button--wide" type="submit" disabled={state.busy || !isSupabaseConfigured}>{state.busy ? "Duke hyrë…" : "Hyr në Dashboard"}</button>
          {state.error ? <div className="admin-inline-error" role="alert">{state.error}</div> : null}
        </form>
      </section>
    </div>
  );
}

function StatCard({ icon, value, label, note, onClick }) {
  return (
    <button className="admin-stat-card" type="button" onClick={onClick}>
      <span className="admin-stat-card__icon"><Icon name={icon} size={21} /></span>
      <strong>{value}</strong>
      <span>{label}</span>
      <small>{note}</small>
    </button>
  );
}

function attachmentsFor(row) {
  if (Array.isArray(row?.attachments)) return row.attachments;
  if (typeof row?.attachments === "string") {
    try {
      const parsed = JSON.parse(row.attachments);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function AttachmentCell({ row, compact, notify }) {
  const attachments = attachmentsFor(row);
  if (!attachments.length) return <span className="attachment-empty">—</span>;
  if (compact) return <span className="attachment-count"><Icon name="document" size={13} /> {attachments.length}</span>;

  const download = async (attachment) => {
    try {
      const url = await getDocumentDownloadUrl(attachment);
      const link = document.createElement("a");
      link.href = url;
      link.rel = "noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      notify?.("error", `Dokumenti nuk u hap: ${error.message}`);
    }
  };

  return (
    <div className="attachment-list">
      {attachments.map((attachment, index) => (
        <button type="button" key={attachment.path || `${attachment.name}-${index}`} onClick={() => download(attachment)} title="Lidhja skadon pas 60 sekondash">
          <Icon name="document" size={13} /> {attachment.name || `Dokumenti ${index + 1}`}
        </button>
      ))}
    </div>
  );
}

function MessageTable({ dataset, onUpdate, onDelete, notify, onOpenCase, compact = false }) {
  if (!dataset?.table) return <DataNotice dataset={dataset} />;
  if (!dataset.rows.length) return <div className="admin-empty">Nuk ka mesazhe.</div>;
  const rows = compact ? dataset.rows.slice(0, 5) : dataset.rows;
  const legacy = dataset.table === "messages";

  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead><tr><th>Emri</th><th>Referenca</th><th>Kontakt</th><th>Çështja</th><th>Dokumente</th><th>Data</th><th>Statusi</th>{compact ? null : <th>Veprime</th>}</tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td><strong>{row.name || "Pa emër"}</strong><small>{row.phone || ""}</small></td>
              <td>{row.case_reference ? <button className="case-reference-link" type="button" onClick={() => onOpenCase?.(row.case_id)}>{row.case_reference}</button> : <span className="attachment-empty">—</span>}</td>
              <td>{row.email || "—"}</td>
              <td><span className="table-message">{row.service ? `${row.service}: ` : ""}{row.message || "—"}</span></td>
              <td><AttachmentCell row={row} compact={compact} notify={notify} /></td>
              <td>{formatDate(row.created_at)}</td>
              <td>
                {compact ? <span className={`status-pill ${legacy ? (row.read ? "status-pill--done" : "") : ""}`}>{legacy ? (row.read ? "READ" : "NEW") : statusLabel(row.status)}</span> : (
                  legacy ? (
                    <button className="table-action" type="button" onClick={() => onUpdate(dataset.table, row.id, { read: !row.read })}>{row.read ? "Shëno si i ri" : "Shëno si lexuar"}</button>
                  ) : (
                    <select value={row.status || "NEW"} onChange={(event) => onUpdate(dataset.table, row.id, { status: event.target.value })}>
                      <option value="NEW">NEW</option><option value="OPEN">OPEN</option><option value="REPLIED">REPLIED</option><option value="CLOSED">CLOSED</option>
                    </select>
                  )
                )}
              </td>
              {compact ? null : <td><button className="icon-action icon-action--danger" type="button" onClick={() => onDelete(dataset.table, row.id, "mesazhin")} aria-label="Fshi mesazhin"><Icon name="trash" size={16} /></button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConsultationTable({ dataset, onUpdate, onDelete, compact = false }) {
  if (!dataset?.table) return <DataNotice dataset={dataset} />;
  if (!dataset.rows.length) return <div className="admin-empty">Nuk ka kërkesa për konsultim.</div>;
  const rows = compact ? dataset.rows.slice(0, 5) : dataset.rows;

  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead><tr><th>Emri</th><th>Shërbimi</th><th>Orari / Data</th><th>Statusi</th>{compact ? null : <th>Veprime</th>}</tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td><strong>{row.name || "Pa emër"}</strong><small>{row.email || row.phone || ""}</small></td>
              <td>{row.service || "—"}</td>
              <td>{formatDate(row.appointment_date || row.created_at)}</td>
              <td>
                {compact ? <span className="status-pill">{statusLabel(row.status)}</span> : (
                  <select value={row.status || (dataset.table === "appointments" ? "REQUESTED" : "new")} onChange={(event) => onUpdate(dataset.table, row.id, { status: event.target.value })}>
                    <option value={dataset.table === "appointments" ? "REQUESTED" : "new"}>REQUESTED</option>
                    <option value="CONFIRMED">CONFIRMED</option><option value="COMPLETED">COMPLETED</option><option value="CANCELLED">CANCELLED</option>
                  </select>
                )}
              </td>
              {compact ? null : <td><button className="icon-action icon-action--danger" type="button" onClick={() => onDelete(dataset.table, row.id, "konsultimin")} aria-label="Fshi konsultimin"><Icon name="trash" size={16} /></button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EditorPanel({ dataset, title, description, fields, emptyValues, onSaved, notify }) {
  const [draft, setDraft] = useState(emptyValues);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const startEdit = (row) => {
    const next = { ...emptyValues };
    fields.forEach((field) => { next[field.name] = row[field.name] ?? next[field.name] ?? (field.type === "checkbox" ? false : ""); });
    setDraft(next);
    setEditingId(row.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setDraft(emptyValues);
    setEditingId(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!dataset.table) return;
    setBusy(true);
    try {
      const payload = {};
      fields.forEach((field) => {
        const value = draft[field.name];
        payload[field.name] = field.type === "number" ? Number(value || 0) : value;
      });
      if (editingId) await updateRow(dataset.table, editingId, payload);
      else await insertRow(dataset.table, payload);
      notify("success", editingId ? "Ndryshimet u ruajtën." : "Regjistrimi u shtua.");
      reset();
      await onSaved();
    } catch (error) {
      notify("error", error.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`A jeni të sigurt që dëshironi të fshini “${row.title || row.name || "këtë regjistrim"}”?`)) return;
    try {
      await deleteRow(dataset.table, row.id);
      notify("success", "Regjistrimi u fshi.");
      await onSaved();
    } catch (error) {
      notify("error", error.message);
    }
  };

  return (
    <div className="admin-editor-layout">
      <section className="admin-panel admin-editor-form">
        <div className="admin-panel__heading"><div><span>Menaxhim</span><h2>{editingId ? `Ndrysho ${title}` : `Shto ${title}`}</h2><p>{description}</p></div>{editingId ? <button className="table-action" type="button" onClick={reset}>Anulo</button> : null}</div>
        <DataNotice dataset={dataset} />
        {dataset.table ? (
          <form onSubmit={submit}>
            {fields.map((field) => (
              field.type === "checkbox" ? (
                <label className="admin-checkbox" key={field.name}><input type="checkbox" checked={Boolean(draft[field.name])} onChange={(event) => setDraft({ ...draft, [field.name]: event.target.checked })} /><span>{field.label}</span></label>
              ) : (
                <label key={field.name}>{field.label}
                  {field.type === "textarea" ? <textarea rows={field.rows || 4} value={draft[field.name] ?? ""} onChange={(event) => setDraft({ ...draft, [field.name]: event.target.value })} required={field.required} /> : <input type={field.type || "text"} value={draft[field.name] ?? ""} onChange={(event) => setDraft({ ...draft, [field.name]: event.target.value })} required={field.required} />}
                </label>
              )
            ))}
            <button className="button button--gold button--wide" type="submit" disabled={busy}>{busy ? "Duke ruajtur…" : (editingId ? "Ruaj Ndryshimet" : "Shto Regjistrimin")}</button>
          </form>
        ) : null}
      </section>

      <section className="admin-panel admin-record-list">
        <div className="admin-panel__heading"><div><span>Regjistrimet</span><h2>{title}</h2><p>{dataset.rows.length} gjithsej</p></div></div>
        {!dataset.rows.length ? <div className="admin-empty">Nuk ka regjistrime.</div> : dataset.rows.map((row) => (
          <article className="admin-record" key={row.id}>
            <div>
              <strong>{row.title || row.name || row.logo_name || "Pa titull"}</strong>
              <p>{String(row.description || row.bio || row.content || row.url || row.logo_url || "").slice(0, 150)}</p>
              <small>{row.active === false || row.published === false ? "Jo publik" : "Aktiv"}</small>
            </div>
            <div className="admin-record__actions">
              <button className="icon-action" type="button" onClick={() => startEdit(row)} aria-label="Ndrysho"><Icon name="edit" size={16} /></button>
              <button className="icon-action icon-action--danger" type="button" onClick={() => remove(row)} aria-label="Fshi"><Icon name="trash" size={16} /></button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

export default function AdminApp() {
  const { t } = useI18n();
  const [phase, setPhase] = useState("loading");
  const [session, setSession] = useState(null);
  const [deniedReason, setDeniedReason] = useState("");
  const [activeSection, setActiveSection] = useState("dashboard");
  const [data, setData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  const authorise = async (nextSession) => {
    setSession(nextSession || null);
    if (!nextSession?.user) {
      setPhase("login");
      return;
    }
    setPhase("authorising");
    const result = await verifyAdmin(nextSession.user);
    if (!result.allowed) {
      setDeniedReason(result.reason);
      setPhase("denied");
      return;
    }
    setPhase("dashboard");
  };

  useEffect(() => {
    let active = true;
    getSession().then((current) => active && authorise(current)).catch((error) => {
      if (active) { setDeniedReason(error.message); setPhase("login"); }
    });
    const unsubscribe = onAuthChange((next) => { if (active) authorise(next); });
    return () => { active = false; unsubscribe(); };
  }, []);

  const refresh = async () => {
    const result = await loadAdminData();
    setData(result);
  };

  useEffect(() => {
    if (phase !== "dashboard") return;
    refresh().catch((error) => setNotice({ type: "error", message: error.message }));
  }, [phase]);

  useEffect(() => {
    if (phase !== "dashboard") return undefined;
    const timer = window.setInterval(() => {
      refresh().catch(() => { /* Manual refresh still reports visible errors. */ });
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [phase]);

  const notify = (type, message) => {
    setNotice({ type, message });
    window.setTimeout(() => setNotice(null), 5000);
  };

  const mutate = async (table, id, values) => {
    try {
      await updateRow(table, id, values);
      notify("success", "Ndryshimi u ruajt.");
      await refresh();
    } catch (error) {
      notify("error", error.message);
    }
  };

  const remove = async (table, id, label) => {
    if (!window.confirm(`A jeni të sigurt që dëshironi të fshini ${label}?`)) return;
    try {
      await deleteRow(table, id);
      notify("success", "Regjistrimi u fshi.");
      await refresh();
    } catch (error) {
      notify("error", error.message);
    }
  };

  const logout = async () => {
    try { await signOut(); } catch (error) { notify("error", error.message); }
  };

  const counts = useMemo(() => {
    if (!data) return { messages: 0, consultations: 0, articles: 0, services: 0, resources: 0, cases: 0, tasks: 0 };
    return {
      messages: data.messages.rows.filter((row) => row.read === false || String(row.status || "NEW").toUpperCase() === "NEW").length,
      consultations: data.consultations.rows.filter((row) => ["REQUESTED", "NEW", "PENDING"].includes(String(row.status || "REQUESTED").toUpperCase())).length,
      articles: data.articles.rows.filter((row) => row.published !== false).length,
      services: data.services.rows.filter((row) => row.active !== false).length,
      resources: data.resources.rows.filter((row) => row.active !== false).length,
      cases: data.cases.rows.filter((row) => !["COMPLETED", "CLOSED"].includes(row.status)).length,
      tasks: data.tasks.rows.filter((row) => ["TODO", "IN_PROGRESS"].includes(row.status)).length,
    };
  }, [data]);

  if (phase === "loading" || phase === "authorising") {
    return <div className="admin-loading"><span className="loader" /><p>{phase === "authorising" ? "Duke verifikuar rolin admin…" : "Duke hapur panelin…"}</p></div>;
  }

  if (phase === "login") return <LoginView onAuthenticated={authorise} />;

  if (phase === "denied") {
    return (
      <div className="admin-denied">
        <span className="icon-badge"><Icon name="shield" size={30} /></span>
        <h1>Qasja nuk u lejua</h1>
        <p>{deniedReason}</p>
        <p className="admin-denied__hint">Kontrolloni që përdoruesi të ketë `role = 'admin'` në `user_roles` dhe që politika RLS t’i lejojë përdoruesit të lexojë rolin e vet.</p>
        <button className="button button--outline" type="button" onClick={logout}>Dil</button>
      </div>
    );
  }

  if (!data) return <div className="admin-loading"><span className="loader" /><p>Duke ngarkuar të dhënat…</p></div>;

  const sectionTitle = adminNavigation.find((item) => item.id === activeSection)?.label || "Dashboard";
  const changeSection = (id) => { setActiveSection(id); setMenuOpen(false); if (id !== "cases" && id !== "tasks") setSelectedCaseId(null); };
  const openCase = (id) => { setSelectedCaseId(id || null); if (id) setActiveSection("cases"); };

  const editorConfig = {
    services: { dataset: data.services, title: "Shërbim", description: "Shtoni, ndryshoni, renditni ose fshihni shërbimet.", fields: serviceFields, emptyValues: { title: "", title_en: "", title_sr: "", description: "", description_en: "", description_sr: "", icon: "scale", sort_order: data.services.rows.length + 1, active: true } },
    resources: { dataset: data.resources, title: "Burim Ligjor", description: "Menaxhoni lidhjet e burimeve zyrtare të Kosovës.", fields: resourceFields, emptyValues: { title: "", title_en: "", title_sr: "", description: "", description_en: "", description_sr: "", url: "", active: true } },
    articles: { dataset: data.articles, title: "Artikull", description: "Shkruani dhe publikoni këshilla ligjore.", fields: articleFields, emptyValues: { title: "", title_en: "", title_sr: "", content: "", content_en: "", content_sr: "", image_url: "", published: false } },
    profile: { dataset: data.profile, title: "Profil", description: "Përditësoni biografinë dhe kontaktet e avokates.", fields: profileFields, emptyValues: { name: "", title: "", title_en: "", title_sr: "", bio: "", bio_en: "", bio_sr: "", phone: "", whatsapp: "", viber: "", email: "", photo_url: "" } },
    branding: { dataset: data.branding, title: "Logo / Brandim", description: "Zgjidhni logon aktive dhe fotografitë e faqes.", fields: brandingFields, emptyValues: { logo_name: "", logo_url: "", hero_image: "", primary_colour: "#c9972b", secondary_colour: "#090b0b", is_active: false } },
  };

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <button className="admin-mobile-toggle" type="button" onClick={() => setMenuOpen((open) => !open)} aria-label="Hap menynë"><Icon name={menuOpen ? "close" : "menu"} size={23} /></button>
        <a href="/" aria-label="Faqja publike"><Brand compact /></a>
        <div className="admin-topbar-actions"><LanguageSwitcher compact /><div className="admin-user"><div><strong>{session?.user?.email || "Administrator"}</strong><span>Administratore</span></div><span className="admin-avatar">{(session?.user?.email || "A").charAt(0).toUpperCase()}</span></div></div>
      </header>

      <aside className={`admin-sidebar ${menuOpen ? "admin-sidebar--open" : ""}`}>
        <nav>
          {adminNavigation.map((item) => (
            <button className={activeSection === item.id ? "active" : ""} key={item.id} type="button" onClick={() => changeSection(item.id)}>
              <Icon name={item.icon} size={17} /><span>{item.label}</span>
              {item.id === "messages" && counts.messages ? <small>{counts.messages}</small> : null}
              {item.id === "consultations" && counts.consultations ? <small>{counts.consultations}</small> : null}
              {item.id === "cases" && counts.cases ? <small>{counts.cases}</small> : null}
              {item.id === "tasks" && counts.tasks ? <small>{counts.tasks}</small> : null}
            </button>
          ))}
        </nav>
        <button className="admin-logout" type="button" onClick={logout}><Icon name="logout" size={17} /> Dalja</button>
      </aside>

      <main className="admin-main">
        <div className="admin-page-title"><div><span>Juris Prudent Admin</span><h1>{sectionTitle}</h1></div><button className="button button--outline button--small" type="button" onClick={refresh}><Icon name="refresh" size={15} /> Rifresko</button></div>
        {notice ? <div className={`admin-notice admin-notice--${notice.type}`} role="status">{notice.message}</div> : null}

        {["dashboard", "cases", "tasks"].includes(activeSection) ? <CaseManagement view={activeSection} data={data} userId={session?.user?.id} selectedCaseId={selectedCaseId} onOpenCase={openCase} onNavigate={changeSection} onRefresh={refresh} notify={notify} /> : null}

        {activeSection === "billing" ? <InvoiceWorkspace data={data} userId={session?.user?.id} onRefresh={refresh} notify={notify} onOpenCase={openCase} /> : null}

        {activeSection === "messages" ? <section className="admin-panel"><div className="admin-panel__heading"><div><span>Inbox</span><h2>Të gjitha mesazhet</h2><p>{data.messages.rows.length} mesazhe</p></div></div><MessageTable dataset={data.messages} onUpdate={mutate} onDelete={remove} notify={notify} onOpenCase={openCase} /></section> : null}
        {activeSection === "consultations" ? <section className="admin-panel"><div className="admin-panel__heading"><div><span>Takimet</span><h2>Kërkesat për konsultim</h2><p>{data.consultations.rows.length} kërkesa</p></div></div><ConsultationTable dataset={data.consultations} onUpdate={mutate} onDelete={remove} /></section> : null}
        {editorConfig[activeSection] ? <EditorPanel {...editorConfig[activeSection]} onSaved={refresh} notify={notify} /> : null}
      </main>
    </div>
  );
}
